import { defineResource } from '@tnn/core'
import { z } from 'zod'
import { signingField } from '../signature'
import { whtOn } from './invoice'

/**
 * Money a customer pays the company (ใบรับเงิน): settling one or more of their invoices, in
 * part or in full, or in advance — a deposit on a charter job (มัดจำ) set against its
 * invoice later. What it settles is the money received, plus the withholding tax the
 * customer deducted (backed by their 50 ทวิ certificate), plus a small difference written off
 * (a transfer fee, rounding). That is allocated to invoices; what is not stays with the
 * customer as a credit (`unallocated`) until it is allocated. Submitting numbers it
 * (`RV6910-0001`) and settles its invoices; cancelling (with a reason) undoes that. Allocations
 * can be changed after submit, e.g. to set a deposit against the job's invoice.
 */
export const paymentKinds = ['payment', 'deposit'] as const
export type PaymentKind = (typeof paymentKinds)[number]
/** How money reaches accounting; a cheque's number goes in `reference` */
export const accountPaymentMethods = ['transfer', 'cash', 'cheque'] as const
export type AccountPaymentMethod = (typeof accountPaymentMethods)[number]

export const moneyInSchema = z.object({
  method: z.enum(accountPaymentMethods),
  amount: z.number().positive(),
  /** The bank reference, or the cheque number and bank */
  reference: z.string().trim().max(100).nullish(),
  /** A photo of the slip or cheque (file id) */
  slipId: z.string().nullish(),
})
export type MoneyIn = z.infer<typeof moneyInSchema>

/** How much of what a payment settles goes to one invoice (no more than it still owes) */
export const allocationSchema = z.object({
  invoiceId: z.string().min(1),
  amount: z.number().positive(),
})
export type Allocation = z.infer<typeof allocationSchema>

const text = (max: number) => z.string().trim().max(max).nullish()

export const payment = defineResource({
  app: 'accounting',
  name: 'payment',
  path: 'payments',
  schema: z.object({
    customerId: z.string().min(1),
    kind: z.enum(paymentKinds).default('payment'),
    receivedOn: z.iso.date(),
    /** Paying a billing note: the note whose invoices it settles (shown on the receipt) */
    billingNoteId: z.string().nullish(),
    /** Deposits: the charter job (tms.shipment) it is paid for */
    shipmentId: z.string().nullish(),
    payments: z.array(moneyInSchema).min(1),
    allocations: z.array(allocationSchema).default([]),
    /** Withheld by the customer instead of paid; counts towards what is settled; 0 for an individual (`canWithhold`) */
    whtAmount: z.number().min(0).default(0),
    /** The customer's withholding tax certificate (หนังสือรับรองการหักภาษี ณ ที่จ่าย, 50 ทวิ) */
    whtCertNo: text(50),
    whtCertFileId: z.string().nullish(),
    /** A small difference accepted as settled (bank fee, rounding), with why */
    writeOff: z.number().min(0).default(0),
    writeOffReason: text(200),
    notes: z.string().max(1000).nullish(),
  }),
  fields: {
    number: { readOnly: true, search: true, list: { width: 140, priority: 1 } },
    receivedOn: { filter: true, list: { width: 130, priority: 1 }, form: { span: 3 } },
    customerId: {
      type: 'ref',
      ref: { resource: 'accounting.customer' },
      filter: true,
      search: true,
      list: { priority: 1 },
      // Warns of what the printed document will go without (PartnerField)
      form: { span: 6, custom: true },
    },
    kind: {
      filter: true,
      tones: { payment: 'success', deposit: 'info' },
      list: { width: 120, priority: 2 },
      form: { span: 3 },
    },
    shipmentId: { type: 'ref', ref: { resource: 'tms.shipment' }, filter: true, list: { priority: 3, hidden: true }, form: { span: 6 } },
    billingNoteId: {
      type: 'ref',
      ref: { resource: 'accounting.billingNote' },
      filter: true,
      list: { priority: 3, hidden: true },
      form: { span: 6 },
    },
    payments: { type: 'json', list: false, form: { span: 12, section: 'money', custom: true }, import: false },
    writeOff: { type: 'money', list: { align: 'end', priority: 3, hidden: true }, form: { span: 4, section: 'money' } },
    writeOffReason: { list: false, form: { span: 8, section: 'money' } },
    // Drawn by the app only for companies (an individual cannot withhold)
    whtAmount: { type: 'money', list: { align: 'end', priority: 3, hidden: true }, form: { span: 4, section: 'wht', custom: true } },
    whtCertNo: { search: true, list: false, form: { span: 4, section: 'wht', custom: true } },
    whtCertFileId: { type: 'file', list: false, form: { span: 4, section: 'wht', custom: true }, import: false, export: false },
    allocations: { type: 'json', list: false, form: { span: 12, section: 'allocations', custom: true }, import: false },
    /** All the money received, every part */
    received: { readOnly: true, type: 'money', list: { align: 'end', width: 140, priority: 1 } },
    /** Received + withholding tax + written off */
    settled: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    allocated: { readOnly: true, type: 'money', list: { align: 'end', priority: 3, hidden: true } },
    /** Settled but not yet set against an invoice: the customer's credit (a deposit until it is used) */
    unallocated: { readOnly: true, type: 'money', list: { align: 'end', width: 140, priority: 2 } },
    notes: { type: 'textarea', list: false, form: { span: 12, section: 'other' } },
    /** The customer's line: signed for, a link waiting, or not yet (server-set) */
    signing: signingField,
  },
  title: 'number',
  columns: [
    'number',
    'receivedOn',
    'customerId',
    'kind',
    'received',
    'unallocated',
    'signing',
    'whtAmount',
    'settled',
    'allocated',
    'shipmentId',
  ],
  sections: ['main', 'money', 'wht', 'allocations', 'other'],
  defaultSort: '-receivedOn',
  actions: ['read', 'create', 'update', 'delete', 'export', 'print'],
  submittable: { cancelReason: 'required' },
  card: { title: 'customerId', subtitle: 'number', meta: ['receivedOn', 'received'], status: 'kind' },
})

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100

export interface PaymentTotals {
  received: number
  /** Received + withholding tax + written off */
  settled: number
  allocated: number
  /** Settled less allocated; below 0 when more was allocated than settled (refused) */
  unallocated: number
}

/** Same numbers on the payment form (live) and the API (stored) */
export function paymentTotals(
  money: readonly Pick<MoneyIn, 'amount'>[],
  allocations: readonly Pick<Allocation, 'amount'>[],
  whtAmount = 0,
  writeOff = 0,
): PaymentTotals {
  const received = round2(money.reduce((sum, m) => sum + (m.amount || 0), 0))
  const settled = round2(received + (whtAmount || 0) + (writeOff || 0))
  const allocated = round2(allocations.reduce((sum, a) => sum + (a.amount || 0), 0))
  return { received, settled, allocated, unallocated: round2(settled - allocated) }
}

/**
 * Spreads money over invoices in the order given (oldest first), each up to what it still owes.
 * With `whtRatio` (the share of an invoice the customer withholds, `whtOn(invoice, total) / total`)
 * each baht received settles more than a baht: the withheld tax makes up the rest.
 */
export function allocateInOrder(invoices: readonly { id: string; outstanding: number; whtRatio?: number }[], money: number): Allocation[] {
  let left = round2(money)
  const allocations: Allocation[] = []
  for (const inv of invoices) {
    if (left <= 0) break
    const net = 1 - (inv.whtRatio ?? 0)
    const amount = round2(Math.min(inv.outstanding, left / net))
    if (amount > 0) allocations.push({ invoiceId: inv.id, amount })
    left = round2(left - amount * net)
  }
  return allocations
}

/** An invoice as a payment settles it: its totals, its withholding rate, what it still owes */
export interface OwedInvoice {
  id: string
  subtotal: number
  total: number
  whtRate: number
  outstanding: number
}

/**
 * What money received settles of these invoices, oldest first, as a payment taken in one step does: with `withholding`
 * each invoice's share of withholding tax is settled too, and a `writeOff` (a transfer fee, rounding) makes up the
 * rest. `due` is what settles them in full; `short` what would still be owed after this money, `over` what is kept as
 * the customer's credit.
 */
export function settlementOf(invoices: readonly OwedInvoice[], received: number, { withholding = false, writeOff = 0 } = {}) {
  const withheld = (inv: OwedInvoice, amount: number) => (withholding ? whtOn(inv, amount) : 0)
  const due = round2(invoices.reduce((sum, inv) => sum + inv.outstanding - withheld(inv, inv.outstanding), 0))
  const allocations = allocateInOrder(
    invoices.map((inv) => ({
      id: inv.id,
      outstanding: inv.outstanding,
      whtRatio: inv.total > 0 ? withheld(inv, inv.total) / inv.total : 0,
    })),
    received + writeOff,
  )
  const byId = new Map(invoices.map((inv) => [inv.id, inv]))
  const whtAmount = round2(allocations.reduce((sum, a) => sum + withheld(byId.get(a.invoiceId) as OwedInvoice, a.amount), 0))
  // A cent's rounding past what came in: the last invoice settled takes it
  const last = allocations.at(-1)
  const gap = paymentTotals([{ amount: received }], allocations, whtAmount, writeOff).unallocated
  if (last && gap < 0 && gap > -0.05) last.amount = round2(last.amount + gap)
  const totals = paymentTotals([{ amount: received }], allocations, whtAmount, writeOff)
  return { due, allocations, whtAmount, short: round2(Math.max(0, due - received - writeOff)), over: Math.max(0, totals.unallocated) }
}
